import { onSnapshot } from 'firebase/firestore';
import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { type CompteDuSuivi, type LireEtatDuChatter, compterLeSuiviPar } from './conversations-chatter.js';
import {
  ConversationSchema,
  ConversationCreateInputSchema,
  ConversationUpdateInputSchema,
  SansPersonne,
  accesACreer,
  cleDeCanal,
  cleDeChatter,
  cleDeDirect,
  cleDeNomDeCanal,
  conversationIdDe,
  estAncreDeFiche,
  MESSAGE_TYPE_CONVERSATION,
  type AncreDeConversation,
  type Conversation,
  type ConversationCreateInput,
  type ConversationUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type CountOptions,
  type ListOptions,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';
import { type ObserveOps, createObserveListOps } from './observe-ops.js';
import { type AccesOps, type MaPersonne, cadrageAcces, createAccesOps } from './acces-par-fiche.js';
import { type LectureDeConversations, createLectureDeConversations } from './conversations-lecture.js';
import {
  type PreferencesConversationsRepository,
  createPreferencesConversations,
} from './conversations-preferences.js';

/**
 * LA PHRASE DU REFUS D'UN NOM DE CANAL EN DOUBLE, UNE SEULE (essai du soir,
 * ecart 4) : le SDK la dit, que le doublon soit vu par lui (`canalParNom`) ou
 * par le serveur (l'id derive de la cle, refuse par les rules) ; une app
 * l'affiche telle quelle (`CanalEnDouble`), sans phrase a elle.
 */
export function messageCanalEnDouble(nom: string): string {
  return `Le canal « ${nom.trim()} » existe déjà (les noms ne diffèrent pas par la casse ni les accents).`;
}

/** Un nom de canal deja pris dans l'org (meme cle : casse et accents ignores). */
export class CanalEnDouble extends Error {
  constructor(public readonly nom: string) {
    super(messageCanalEnDouble(nom));
    this.name = 'CanalEnDouble';
  }
}

/** Le chatter ne vit que sur une FICHE (vague du soir, R9) : une ancre de porte ou d'app est refusee. */
export class ChatterHorsFiche extends Error {
  constructor() {
    super("Le suivi ne vit que sur une fiche : une ancre de porte ou d'app n'est plus acceptée.");
    this.name = 'ChatterHorsFiche';
  }
}

/** Message du refus d'ecrire dans le chatter d'une fiche dont la base a le chatter eteint (G16). */
export const MESSAGE_CHATTER_ETEINT = 'Le suivi est éteint pour les fiches de cette base : le message n’est pas envoyé.';

/** La base de cette fiche a « Suivi sur les fiches » eteint : on n'y ecrit pas (verification independante, ecart C). */
export class ChatterEteint extends Error {
  constructor() {
    super(MESSAGE_CHATTER_ETEINT);
    this.name = 'ChatterEteint';
  }
}

/**
 * UN MESSAGE DIRECT NE SE SUPPRIME PAS (P18 du 06/10/2026, decision du
 * fondateur) : ni archive pour tous, ni masque pour soi. Le masquer le
 * faisait disparaitre pour de bon, `creerDirect` reprenant ensuite le meme
 * document sans le reafficher.
 */
export class DirectNonSupprimable extends Error {
  constructor() {
    super('Une conversation directe ne peut pas être supprimée.');
    this.name = 'DirectNonSupprimable';
  }
}

/** Le geste n'est pas permis a l'appelant sur cette conversation (renommer, archiver). */
export class GesteRefuse extends Error {
  constructor(geste: string, raison: string) {
    super(`${geste} : ${raison}`);
    this.name = 'GesteRefuse';
  }
}

/**
 * Repository de la famille `conversations` : un fil d'echanges (un sujet avec
 * Lia, un canal, un message direct, le chatter d'une fiche), la premiere
 * famille PORTEUSE d'un acces par fiche (`docs/socle/acces-par-fiche.md`).
 *
 * Meme patron que `message` pour les verbes standard. Ce que la famille
 * ajoute : `acces` a la creation (equipe par defaut ; prive avec ses
 * participants, le proprietaire pose par le SDK ; ancre sur une fiche), les
 * trois gestes sur les participants (`partager`, `retirer`, `quitter`),
 * `observe` (le centre de communication voit arriver un titre ou un
 * participant), et l'option `acces` des listes (`'equipe'`, `'prive'`,
 * `{ ancre }`).
 */

export type { ConversationCreateInput, AncreDeConversation };

/** Nom de la collection Firestore de la famille conversation. */
export const CONVERSATIONS_COLLECTION = 'conversations';

/** Ce que `creerCanal` recoit. */
export interface CanalACreer {
  nom: string;
  description?: string;
  /** Un canal prive : ses participants (personIds), l'appelant ajoute et proprietaire. */
  prive?: boolean;
  participants?: readonly string[];
}

/** Le tri des canaux : par nom, en francais. */
export function trierCanaux(canaux: readonly Conversation[]): Conversation[] {
  return [...canaux].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
}

/**
 * NE PUBLIER QU'UN INSTANTANE ACQUITTE. `observeList` rend d'abord l'instantane
 * LOCAL d'une ecriture en attente (`meta.local`) : une conversation qu'on
 * vient de creer y figure AVANT que les rules l'aient acceptee. Un ecran qui
 * ouvrait aussitot ses messages tombait sur « Property acces is undefined »
 * (la conversation n'existe pas encore cote serveur) et purgeait le canal
 * (Canaux, 05/10/2026 15 h 32). Les observateurs de ce depot ne publient
 * donc que les instantanes acquittes ; la conversation arrive au tour
 * suivant, une fois ecrite. Exporte pour le test.
 */
export function siAcquitte<T>(onChange: (liste: T) => void): (liste: T, meta: { local: boolean }) => void {
  return (liste, meta) => {
    if (meta.local) return;
    onChange(liste);
  };
}

export interface ConversationRepository extends ObserveOps<Conversation>, AccesOps<Conversation> {
  // ---- Le contrat conversations du 05/10/2026 (docs/socle/conversations.md) ----
  /** Un canal, public (defaut) ou prive avec ses participants. */
  creerCanal(input: CanalACreer): Promise<Conversation>;
  /** Le direct entre l'appelant et ces personnes : le rend s'il existe deja, sinon le cree. */
  creerDirect(personIds: readonly string[]): Promise<Conversation>;
  /** Le chatter de cette ancre, ou `null` tant qu'aucun message n'y a ete ecrit. Un seul `get`. */
  chatterDe(ancre: AncreDeConversation): Promise<Conversation | null>;
  /** Le chatter de cette ancre, cree au premier message, retrouve ensuite. Refuse (`ChatterEteint`) si la base l'a eteint. */
  ouvrirChatter(ancre: AncreDeConversation): Promise<Conversation>;
  /**
   * La base de cette fiche a-t-elle « Suivi sur les fiches » allume ? Lu au
   * serveur (la meme lecture que la garde) ; serveur injoignable : `true`, la
   * garde serveur archive de toute facon. La fenetre et Canaux masquent
   * l'epingle d'un chatter eteint, qui revient au rallumage.
   */
  chatterActif(ancre: AncreDeConversation): Promise<boolean>;
  /** Leve `ChatterEteint` si cette conversation est le chatter d'une fiche dont la base a le chatter eteint. `sdk.messages.envoyer` l'appelle. */
  verifierEcriture(conversationId: string): Promise<void>;
  /**
   * LES COMPTES DU SUIVI d'une fiche (son chatter), par trois `count()`
   * serveur : le total des entrees du fil, puis les messages (personnes et
   * Lia, activites comprises), les changements de champs et les autres
   * evenements. Le cercle et la decomposition du rail les lisent ; jamais un
   * compte de la page chargee (AGENTS.md section 7).
   */
  compterLeSuivi(conversationId: string): Promise<CompteDuSuivi>;
  /** Les canaux publics et les canaux prives dont l'appelant est participant, tries par nom. */
  listerCanaux(): Promise<Conversation[]>;
  /**
   * Les messages directs de l'appelant, du plus recent au plus ancien, TOUS :
   * un direct ne se masque plus (P18), et un direct present dans `masquees`
   * (anciennes donnees) redevient visible.
   */
  listerDirects(): Promise<Conversation[]>;
  /**
   * `listerCanaux` en direct : deux ecoutes (publics, prives), une liste
   * fusionnee et triee. Instantanes ACQUITTES seulement (`siAcquitte`) : une
   * conversation qu'on cree apparait une fois ecrite, jamais avant.
   */
  observerSuivies(onChange: (conversations: Conversation[]) => void, onError?: (e: unknown) => void): () => void;
  observerCanaux(onChange: (canaux: Conversation[]) => void, onError?: (error: unknown) => void): () => void;
  /** `listerDirects` en direct. Instantanes acquittes seulement (`siAcquitte`). */
  observerDirects(onChange: (directs: Conversation[]) => void, onError?: (error: unknown) => void): () => void;
  /** La lecture et les non lus (conversations-lecture.ts). */
  readonly lecture: LectureDeConversations;
  /** Les preferences personnelles : epingles, directs masques (conversations-preferences.ts). */
  readonly preferences: PreferencesConversationsRepository;

  // ---- La vague du soir (R4, point e) ----
  /** Renomme un canal (le nom reste unique dans l'org). Gestionnaire seul. */
  renommer(id: string, nom: string): Promise<Conversation>;
  /** Canal : archive pour tous (gestionnaire). Direct : refuse (`DirectNonSupprimable`, P18). Chatter : refuse. */
  archiver(id: string): Promise<void>;
  /** L'inverse d'`archiver` pour un canal. Direct : le retire de `masquees` (anciennes donnees), sans autre effet. */
  restaurer(id: string): Promise<void>;
  /** Les canaux archives que je lis, pour l'app Canaux (R6). Jamais de direct (P18). */
  listerArchivees(): Promise<Conversation[]>;
  /** Le canal de ce nom (meme cle), ou `null`. Un import de demo l'appelle avant de creer. */
  canalParNom(nom: string): Promise<Conversation | null>;
  /** Vrai si l'appelant peut renommer et archiver cette conversation pour tous (canal). Toujours faux pour un direct. */
  peutGerer(conversation: Conversation): Promise<boolean>;

  create(input: ConversationCreateInput): Promise<Conversation>;
  update(id: string, patch: ConversationUpdateInput): Promise<Conversation>;
  list(options?: ListOptions): Promise<Conversation[]>;
  count(options?: CountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: CountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: CountOptions): Promise<number | null>;
  get(id: string): Promise<Conversation | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Conversation>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Conversation>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Conversation>;
}

const conversationConverter = createConverter<Conversation>((raw: DocumentData) =>
  ConversationSchema.parse(raw),
);

/**
 * @param getActorId - l'uid de l'utilisateur courant (les rules exigent
 *   createdBy/updatedBy == request.auth.uid).
 * @param maPersonne - le `personId` de l'appelant (`acces-par-fiche.ts`), pour
 *   le proprietaire d'une conversation privee et le cadrage d'une liste privee.
 */
export function createConversationRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
  maPersonne: MaPersonne,
  monRole: () => Promise<string | null> = async () => null,
  etatDuChatter?: LireEtatDuChatter,
): ConversationRepository {
  const col = collection(db, CONVERSATIONS_COLLECTION).withConverter(conversationConverter);
  const archiveOps = createArchiveOps<Conversation>(db, col, CONVERSATIONS_COLLECTION, getActorId);
  const accesOps = createAccesOps<Conversation>(db, col, CONVERSATIONS_COLLECTION, getActorId, maPersonne);
  const cadrage = (options?: ListOptions | CountOptions) => cadrageAcces('porteuse', options?.acces, maPersonne);
  const observeListOps = createObserveListOps<Conversation>(col, orgId, cadrage);
  const preferences = createPreferencesConversations(db, getActorId, orgId);

  /** La conversation d'une cle : un `get` sur l'id derive ; `null` si absente, supprimee, ou fermee a l'appelant. */
  async function lireParCle(cle: string): Promise<Conversation | null> {
    const id = await conversationIdDe(orgId, cle);
    let snap;
    try {
      snap = await getDoc(doc(col, id));
    } catch (cause) {
      const code = (cause as { code?: unknown } | null)?.code;
      if (code === 'permission-denied') return null;
      throw cause;
    }
    if (!snap.exists()) return null;
    const c = snap.data();
    return c.deletedAt == null ? c : null;
  }

  /** Cree la conversation a l'id derive de sa cle (meme ecriture que `create`, id impose, `cle` posee). */
  async function creerACle(cle: string, input: ConversationCreateInput & { ancre?: { appId: string; doorId?: string } }): Promise<Conversation> {
    const fields = ConversationCreateInputSchema.parse({ ...input, cle });
    const acces = accesACreer(fields.acces, fields.acces?.visibilite === 'prive' ? await maPersonne() : null);
    const actor = getActorId();
    const id = await conversationIdDe(orgId, cle);
    const now = new Date();
    const payload: DocumentData = {
      orgId: fields.orgId,
      type: fields.type,
      name: fields.name,
      cle,
      ...(fields.ancre !== undefined ? { ancre: fields.ancre } : {}),
      acces,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: actor,
      updatedBy: actor,
      archivedAt: null,
      deletedAt: null,
      schemaVersion: CURRENT_SCHEMA_VERSION,
    };
    await setDoc(doc(db, CONVERSATIONS_COLLECTION, id), payload);
    return ConversationSchema.parse({ id, ...payload, createdAt: now, updatedAt: now });
  }

  return {
    ...accesOps,

    async create(input: ConversationCreateInput): Promise<Conversation> {
      const fields = ConversationCreateInputSchema.parse(input);
      // Le proprietaire d'une conversation privee est l'appelant, par son
      // `personId` : une seule lecture, et seulement pour le prive.
      const acces = accesACreer(
        fields.acces,
        fields.acces?.visibilite === 'prive' ? await maPersonne() : null,
      );
      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      const optional: DocumentData = {};
      if (fields.description !== undefined) optional['description'] = fields.description;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;
      if (fields.couleur != null) optional['couleur'] = fields.couleur;
      if (fields.photo != null) optional['photo'] = fields.photo;
      if (fields.cle !== undefined) optional['cle'] = fields.cle;
      if (fields.ancre !== undefined) optional['ancre'] = fields.ancre;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        name: fields.name,
        acces,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Conversation);

      return ConversationSchema.parse({ id: ref.id, ...payload, createdAt: now, updatedAt: now });
    },

    async creerCanal(input) {
      if (typeof input?.nom !== 'string' || cleDeNomDeCanal(input.nom) === '') {
        throw new Error('conversations.creerCanal : un canal porte un nom.');
      }
      const nom = input.nom.trim();
      if ((await this.canalParNom(nom)) !== null) throw new CanalEnDouble(nom);
      try {
        // L'id derive de la cle du nom : deux creations du meme nom visent le
        // meme document, la seconde est refusee par les rules (point e).
        return await creerACle(cleDeCanal(nom), {
          orgId,
          type: 'canal',
          name: nom,
          ...(input.description !== undefined ? { description: input.description } : {}),
          acces: input.prive === true
            ? { visibilite: 'prive', participants: [...(input.participants ?? [])] }
            : { visibilite: 'equipe' },
        });
      } catch (cause) {
        const code = (cause as { code?: unknown } | null)?.code;
        if (code === 'permission-denied' || code === 'already-exists') throw new CanalEnDouble(nom);
        throw cause;
      }
    },

    async canalParNom(nom) {
      const cle = cleDeNomDeCanal(nom);
      if (cle === '') return null;
      const parId = await lireParCle(`canal:${cle}`);
      if (parId !== null) return parId;
      // Les canaux d'avant ce point (id tire au sort) : par la liste, archives compris.
      const [actifs, archives] = await Promise.all([this.listerCanaux(), this.listerArchivees()]);
      return [...actifs, ...archives].find((c) => c.type === 'canal' && cleDeNomDeCanal(c.name) === cle) ?? null;
    },

    async renommer(id, nom) {
      const c = await this.get(id);
      if (c === null) throw new GesteRefuse('renommer', 'conversation introuvable.');
      if (c.type !== 'canal') throw new GesteRefuse('renommer', 'seul un canal porte un nom.');
      if (!(await this.peutGerer(c))) throw new GesteRefuse('renommer', 'réservé au créateur du canal, à son propriétaire, ou à un administrateur.');
      const propre = nom.trim();
      if (cleDeNomDeCanal(propre) === '') throw new GesteRefuse('renommer', 'un canal porte un nom.');
      const deja = await this.canalParNom(propre);
      if (deja !== null && deja.id !== id) throw new CanalEnDouble(propre);
      return this.update(id, { name: propre });
    },

    async archiver(id) {
      const c = await this.get(id);
      if (c === null) throw new GesteRefuse('archiver', 'conversation introuvable.');
      if (c.type === 'direct') throw new DirectNonSupprimable();
      if (c.type !== 'canal') throw new GesteRefuse('archiver', 'un suivi vit avec sa fiche.');
      if (!(await this.peutGerer(c))) throw new GesteRefuse('archiver', 'réservé au créateur du canal, à son propriétaire, ou à un administrateur.');
      await this.archive(id);
    },

    async restaurer(id) {
      const c = await this.get(id);
      if (c === null) throw new GesteRefuse('restaurer', 'conversation introuvable.');
      if (c.type === 'direct') return preferences.afficher(id);
      if (c.type !== 'canal') throw new GesteRefuse('restaurer', 'un suivi vit avec sa fiche.');
      if (!(await this.peutGerer(c))) throw new GesteRefuse('restaurer', 'réservé au créateur du canal, à son propriétaire, ou à un administrateur.');
      await this.unarchive(id);
    },

    async listerArchivees() {
      const [publics, prives] = await Promise.all([
        this.list({ type: 'canal', acces: 'equipe', archived: 'only', limit: 100 }),
        this.list({ type: 'canal', acces: 'prive', archived: 'only', limit: 100 }),
      ]);
      return trierCanaux([...publics, ...prives]);
    },

    async peutGerer(c) {
      if (c.type !== 'canal') return false;
      const acces = c.acces ?? { visibilite: 'equipe' as const };
      if (acces.visibilite === 'prive') return acces.proprietaire === (await maPersonne());
      if (c.createdBy === getActorId()) return true;
      const role = await monRole();
      return role === 'owner' || role === 'admin';
    },

    async creerDirect(personIds) {
      const moi = await maPersonne();
      if (moi === null) throw new SansPersonne();
      const cle = cleDeDirect([moi, ...personIds]);
      const existant = await lireParCle(cle);
      if (existant !== null) {
        // P18 : un direct masque par d'anciennes donnees redevient visible quand on le rouvre.
        const { masquees } = await preferences.lire().catch(() => ({ masquees: [] as string[] }));
        if (masquees.includes(existant.id)) await preferences.afficher(existant.id).catch(() => undefined);
        return existant;
      }
      return creerACle(cle, { orgId, type: 'direct', name: '', acces: { visibilite: 'prive', participants: [...personIds] } });
    },

    async chatterDe(ancre) {
      if (!estAncreDeFiche(ancre)) throw new ChatterHorsFiche();
      return lireParCle(cleDeChatter(ancre));
    },

    async ouvrirChatter(ancre) {
      if (!estAncreDeFiche(ancre)) throw new ChatterHorsFiche();
      const cle = cleDeChatter(ancre);
      const existant = await lireParCle(cle);
      if (existant !== null) return existant;
      if (!(await this.chatterActif(ancre))) throw new ChatterEteint();
      return creerACle(cle, { orgId, type: 'chatter', name: '', acces: { visibilite: 'ancre', ancre } });
    },

    async chatterActif(ancre) {
      if (!estAncreDeFiche(ancre)) throw new ChatterHorsFiche();
      if (etatDuChatter === undefined) return true;
      try { return await etatDuChatter({ family: ancre.family, recordId: ancre.recordId }); }
      catch { return true; }
    },

    async verifierEcriture(conversationId) {
      const snap = await getDoc(doc(col, conversationId)).catch(() => null);
      const c = snap?.exists() ? snap.data() : null;
      const acces = c?.type === 'chatter' ? c.acces : undefined;
      const ancre = acces?.visibilite === 'ancre' ? acces.ancre : null;
      if (ancre && !(await this.chatterActif(ancre))) throw new ChatterEteint();
    },

    async compterLeSuivi(conversationId) {
      if (typeof conversationId !== 'string' || conversationId === '') throw new Error('conversations.compterLeSuivi : une conversation est requise.');
      // Le cadrage d'une liste de messages (famille heritiere : la conversation
      // fixee, que les rules exigent), et des egalites de plus par sorte.
      const messages = collection(db, 'messages');
      const acces = { conversation: conversationId };
      const cadrageDesMessages = await cadrageAcces('heritiere', acces, maPersonne);
      return compterLeSuiviPar((where) => runCount(messages, orgId, {
        type: MESSAGE_TYPE_CONVERSATION,
        acces,
        ...(where !== undefined ? { where: { ...where } } : {}),
      }, cadrageDesMessages));
    },

    async listerCanaux() {
      const [publics, prives] = await Promise.all([
        this.list({ type: 'canal', acces: 'equipe', orderBy: { field: 'name', dir: 'asc' }, limit: 100 }),
        this.list({ type: 'canal', acces: 'prive', orderBy: { field: 'name', dir: 'asc' }, limit: 100 }),
      ]);
      return trierCanaux([...publics, ...prives]);
    },

    async listerDirects() {
      // P18 : `masquees` n'est plus lu pour les directs ; il reste dans les preferences, sans effet ici.
      return this.list({ type: 'direct', acces: 'prive', orderBy: { field: 'updatedAt', dir: 'desc' }, limit: 100 });
    },

    observerSuivies(onChange, onError) {
      let vivant = true, tour = 0;
      let citees: string[] = [], ouvertes: string[] = [], selection: string | null = null;
      const publier = () => {
        const ids = [...new Set([...citees, ...ouvertes])].sort();
        const cle = ids.join('|');
        if (cle === selection) return;
        selection = cle;
        const n = ++tour;
        void Promise.all(ids.map(id => this.get(id).catch(e => {
          if (e?.code === 'permission-denied' || e?.code === 'not-found') return null;
          throw e;
        }))).then(liste => { if (vivant && tour === n) onChange(liste.filter((c): c is Conversation => c !== null && c.type === 'chatter' && c.archivedAt === null && c.deletedAt === null)); }).catch(e => { selection = null; if (vivant) onError?.(e); });
      };
      const personne = doc(db, 'orgs', orgId, 'lectures', getActorId());
      const arrets = [
        onSnapshot(collection(personne, 'conversations'), snap => { citees = snap.docs.map(d => d.id); publier(); }, onError),
        onSnapshot(personne, snap => { ouvertes = Object.keys(snap.data()?.['lu'] ?? {}).filter(id => !id.startsWith('lia:')); publier(); }, onError),
      ];
      return () => { vivant = false; arrets.forEach(arret => arret()); };
    },
    observerCanaux(onChange, onError) {
      let publics: Conversation[] = [];
      let prives: Conversation[] = [];
      const publier = () => onChange(trierCanaux([...publics, ...prives]));
      const arrets = [
        observeListOps.observeList(
          { type: 'canal', acces: 'equipe', orderBy: { field: 'name', dir: 'asc' }, limit: 100 },
          siAcquitte((liste) => { publics = liste; publier(); }),
          onError,
        ),
        observeListOps.observeList(
          { type: 'canal', acces: 'prive', orderBy: { field: 'name', dir: 'asc' }, limit: 100 },
          siAcquitte((liste) => { prives = liste; publier(); }),
          onError,
        ),
      ];
      return () => { for (const arret of arrets) arret(); };
    },

    observerDirects(onChange, onError) {
      // P18 : une seule ecoute ; un direct ne se masque plus, `masquees` est ignore.
      return observeListOps.observeList(
        { type: 'direct', acces: 'prive', orderBy: { field: 'updatedAt', dir: 'desc' }, limit: 100 },
        siAcquitte(onChange),
        onError,
      );
    },

    lecture: createLectureDeConversations(db, getActorId, orgId),
    preferences,

    async update(id: string, patch: ConversationUpdateInput): Promise<Conversation> {
      const fields = ConversationUpdateInputSchema.parse(patch);
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = getActorId();
      await updateDoc(doc(db, CONVERSATIONS_COLLECTION, id), data);
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`conversation ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Conversation[]> {
      return runList(col, orgId, options, await cadrage(options));
    },

    async count(options?: CountOptions): Promise<number> {
      return runCount(col, orgId, options, await cadrage(options));
    },

    async sum(field: string, options?: CountOptions): Promise<number> {
      return runSum(col, orgId, field, options, await cadrage(options));
    },

    async average(field: string, options?: CountOptions): Promise<number | null> {
      return runAverage(col, orgId, field, options, await cadrage(options));
    },

    async get(id: string): Promise<Conversation | null> {
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) return null;
      return snap.data();
    },

    ...archiveOps,
  };
}
